import { apiClient as api } from "./httpClient";

// Anotações gerais do paciente (RN-534): visíveis a todo perfil com acesso ao cadastro;
// o autor altera/exclui só dentro da janela de edição (7 dias). Escrita dupla no Bubble
// (RN-302) — a lista lê o tenant.

export interface PatientNote {
  id: string;
  note: string;
  createdBy: string | null;
  createdByName: string;
  /** ISO no relógio da entidade (sem offset) */
  createdAt: string;
  modifiedAt: string | null;
  /** autor = usuário da sessão */
  isMine: boolean;
  /** autor E dentro da janela de edição — o servidor recusa o resto */
  canEdit: boolean;
  /** RN-534c: 'author' = só quem escreveu vê; 'all' = toda a equipe com o cadastro */
  visibility: NoteVisibility;
  priority: NotePriority;
}

export type NoteVisibility = 'author' | 'all';
export type NotePriority = 'low' | 'medium' | 'high' | 'urgent';

export const NOTE_PRIORITIES: { value: NotePriority; label: string; color: string; bg: string }[] = [
  { value: 'low', label: 'Baixa', color: '#495057', bg: '#e9ecef' },
  { value: 'medium', label: 'Média', color: '#0d6e6e', bg: '#e6f4f2' },
  { value: 'high', label: 'Alta', color: '#856404', bg: '#fff3cd' },
  { value: 'urgent', label: 'Urgência', color: '#842029', bg: '#f8d7da' },
];

export const NOTE_VISIBILITIES: { value: NoteVisibility; label: string }[] = [
  { value: 'author', label: 'Somente eu' },
  { value: 'all', label: 'Toda a equipe' },
];

export interface PatientNoteAuthor {
  id: string;
  name: string;
}

export interface PatientNotesListResult {
  items: PatientNote[];
  totalCount: number;
  /** quem já anotou neste paciente (independe dos filtros) */
  authors: PatientNoteAuthor[];
  /** dias da janela de edição (configuração do produto) */
  editWindowDays: number;
}

export interface PatientNotesQueryParams {
  search?: string;
  createdBy?: string;
  from?: string;
  to?: string;
  pageNumber: number;
  pageSize: number;
}

export const PATIENT_NOTE_MAX_LENGTH = 2000;

export const patientNotesService = {
  getByPatient: async (patientId: string, params: PatientNotesQueryParams): Promise<PatientNotesListResult> => {
    const response = await api.get(`/patient-notes/by-patient/${patientId}`, { params });
    return response.data;
  },

  /** Bubble primeiro (~1s) — timeout largo de propósito */
  create: async (patientId: string, note: string, visibility: NoteVisibility, priority: NotePriority): Promise<string> => {
    const response = await api.post("/patient-notes", { patientId, note, visibility, priority }, { timeout: 60000 });
    return response.data;
  },

  update: async (id: string, note: string, visibility: NoteVisibility, priority: NotePriority): Promise<void> => {
    await api.put(`/patient-notes/${id}`, { note, visibility, priority }, { timeout: 60000 });
  },

  remove: async (id: string): Promise<void> => {
    await api.delete(`/patient-notes/${id}`, { timeout: 60000 });
  },
};

/** Erros de negócio conhecidos em PT-BR; o code vem do Result.Error do backend. */
export const mapPatientNoteError = (error: any): string => {
  const code: string = error?.response?.data?.code ?? "";
  const messages: Record<string, string> = {
    "PatientNotes.NotFound": "Anotação não encontrada (pode ter sido excluída).",
    "PatientNotes.PatientNotFound": "Paciente não encontrado na entidade.",
    "PatientNotes.EmptyNote": "Escreva o texto da anotação.",
    "PatientNotes.TooLong": `A anotação deve ter no máximo ${PATIENT_NOTE_MAX_LENGTH} caracteres.`,
    "PatientNotes.NotAuthor": "Somente quem escreveu a anotação pode alterá-la ou excluí-la.",
    "PatientNotes.EditWindowExpired": "Prazo de edição encerrado: a anotação só pode ser alterada ou excluída em até 7 dias após a criação.",
    "PatientNotes.BubbleWriteFailed": "Não foi possível gravar a anotação agora. Tente novamente em instantes.",
  };
  return messages[code] || "Não foi possível concluir a operação. Tente novamente.";
};
